import { useEffect, useMemo, useRef, useState } from 'react';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import FormattedDateInput from '../../components/ui/FormattedDateInput';
import WizardSelect from '../../components/ui/WizardSelect';
import { ReviewUploadedFilesTable } from '../../components/wizard/ReviewUploadedFilesTable';
import {
  ReviewChips,
  ReviewField,
  ReviewGrid,
  ReviewSection,
  ReviewSubBlock,
} from '../../components/criminal/CriminalReviewUi';
import {
  getCivilAppellateRequestViewData,
  saveCivilAppellateRequest,
  type CivilAppellateRequestViewData,
} from '../../services/civilAppellateCaseRequests';
import type { CivilAppellateWizardStep } from '../../pages/wizard/civilAppellate/civilAppellateConstants';
import { INCORPORATED_BODY_TYPE_LABELS } from '../../pages/wizard/civilAppellate/civilAppellateConstants';
import type { CivilAppellateWizardDraft } from '../../types/civilAppellateCaseRequest';
import type { CriminalSaveStep4 } from '../../types/criminalApi';
import type { CriminalWizardDraft } from '../../types/criminalCaseRequest';
import { isOtherCorporateInstitute } from '../../pages/wizard/civilAppellate/civilAppellateHelpers';
import {
  isHighUrgencyLevel,
  minDeadlineDateForCivilAppellate,
  validateCivilAppellateStep,
} from '../../pages/wizard/civilAppellate/civilAppellateStepValidation';
import { useCivilAppellateLookups } from '../../pages/wizard/civilAppellate/useCivilAppellateLookups';
import {
  deliveryMethodDisplay,
  submitterFromApiStep,
} from '../view-request/viewRequestHelpers';
import {
  CONTACT_NUMBER_LENGTH,
  sliceMax,
} from '../../utils/caseFieldValidation';
import { formatDisplayDateTime } from '../../utils/dateFormat';
import { setFlashToast } from '../../utils/flashToast';
import { focusFirstInvalid } from '../../utils/requiredValidation';
import { FieldError, inpClass } from './criminalFieldUi';
import { omitCaseUpdateUploadErrors, omitCaseUpdateUploadSpecs } from './caseUpdateUploadValidation';
import {
  confirmCaseUpdateCancel,
  formatSubmittedAtLabel,
  isCaseRequestUuid,
  mergeSavedStepIntoInitialBaseline,
  toSelectOpts,
} from './taskCaseUpdateHelpers';
import TaskCaseUpdateSectionActions from './TaskCaseUpdateSectionActions';

type Props = {
  caseRequestId: string;
  recentSubmittedAt: string;
  readOnly?: boolean;
  viewData?: CivilAppellateRequestViewData | null;
  viewDataLoading?: boolean;
  onCancelConfirmed?: () => void;
};

const MAX_DESC = 2000;
const MAX_REF = 255;
const MAX_TEXT = 2000;
const MAX_CONTACT = CONTACT_NUMBER_LENGTH;

function cloneDraft(d: CivilAppellateWizardDraft): CivilAppellateWizardDraft {
  return {
    ...d,
    applicableAreas: [...d.applicableAreas],
    applicableAreaIds: [...d.applicableAreaIds],
    judgmentOrOrderDocuments: d.judgmentOrOrderDocuments.map((f) => ({ ...f })),
    deliveryMethodIds: [...d.deliveryMethodIds],
    deliveryMethods: [...d.deliveryMethods],
    documents: d.documents.map((doc) => ({ ...doc })),
  };
}

function draftHasUnsavedChanges(
  current: CivilAppellateWizardDraft,
  saved: CivilAppellateWizardDraft,
): boolean {
  return JSON.stringify(current) !== JSON.stringify(saved);
}

function pickStep1Snapshot(d: CivilAppellateWizardDraft) {
  return {
    description: d.description,
    isAppealMatter: d.isAppealMatter,
    originalCourt: d.originalCourt,
    judgmentOrOrderDocuments: d.judgmentOrOrderDocuments.map((f) => ({
      id: f.id,
      fileName: f.fileName,
      originalFileName: f.originalFileName,
    })),
    provinceId: d.provinceId,
    districtId: d.districtId,
    yourReferenceNumber: d.yourReferenceNumber,
    otherExternalReferences: d.otherExternalReferences,
    caseNumber: d.caseNumber,
    hasConnectedMatters: d.hasConnectedMatters,
    connectedCaseNumber: d.connectedCaseNumber,
    connectedAgdFileRefAvailable: d.connectedAgdFileRefAvailable,
    connectedAgdFileRef: d.connectedAgdFileRef,
  };
}

function pickStep2Snapshot(d: CivilAppellateWizardDraft) {
  return {
    plaintiffName: d.plaintiffName,
    defendantName: d.defendantName,
    isIncorporatedBody: d.isIncorporatedBody,
    otherInvolvedInstitutions: d.otherInvolvedInstitutions,
    legalOfficerName: d.legalOfficerName,
    legalOfficerContact: d.legalOfficerContact,
  };
}

function pickStep3Snapshot(d: CivilAppellateWizardDraft) {
  return {
    urgencyLevelId: d.urgencyLevelId,
    deadline: d.deadline,
    urgencyJustification: d.urgencyJustification,
  };
}

function stepHasData(step: 1 | 2 | 3, d: CivilAppellateWizardDraft): boolean {
  if (step === 1) {
    return !!(
      d.description.trim() ||
      d.yourReferenceNumber.trim() ||
      d.provinceId.trim() ||
      d.districtId.trim() ||
      d.isAppealMatter
    );
  }
  if (step === 2) {
    return !!(
      d.plaintiffName.trim() ||
      d.defendantName.trim() ||
      d.legalOfficerName.trim() ||
      d.legalOfficerContact.trim()
    );
  }
  return !!(d.urgencyLevelId.trim() || d.deadline.trim() || d.urgencyJustification.trim());
}

function canSaveStep(
  step: 1 | 2 | 3,
  draft: CivilAppellateWizardDraft | null,
  initial: CivilAppellateWizardDraft | null,
): boolean {
  if (!draft || !initial) return false;
  const pick =
    step === 1 ? pickStep1Snapshot : step === 2 ? pickStep2Snapshot : pickStep3Snapshot;
  const changed = JSON.stringify(pick(draft)) !== JSON.stringify(pick(initial));
  return (stepHasData(step, draft) || stepHasData(step, initial)) && changed;
}

export default function TaskCaseCivilAppellateUpdateTab({
  caseRequestId,
  recentSubmittedAt,
  readOnly = false,
  viewData,
  viewDataLoading = false,
  onCancelConfirmed,
}: Props) {
  const [draft, setDraft] = useState<CivilAppellateWizardDraft | null>(null);
  const [initial, setInitial] = useState<CivilAppellateWizardDraft | null>(null);
  const [step4, setStep4] = useState<CivilAppellateRequestViewData['step4']>();
  const [submittedLabel, setSubmittedLabel] = useState(recentSubmittedAt);
  const [loadedForKey, setLoadedForKey] = useState<string | null>(null);
  const [loadError, setLoadError] = useState('');
  const [savingStep, setSavingStep] = useState<CivilAppellateWizardStep | null>(null);
  const saveInFlightRef = useRef(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const isSaving = savingStep !== null;

  const lookups = useCivilAppellateLookups('review', draft?.provinceId ?? '');
  const minDeadlineDate = useMemo(() => minDeadlineDateForCivilAppellate(), []);
  const useSharedViewData = viewData !== undefined || viewDataLoading;
  const loadKey = `${caseRequestId}|${recentSubmittedAt}|${viewData?.submittedAt ?? ''}|${viewData?.draft.draftRequestId ?? ''}`;
  const loading = useSharedViewData
    ? viewDataLoading || loadedForKey !== loadKey
    : loadedForKey !== loadKey;

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (!isCaseRequestUuid(caseRequestId)) {
        if (cancelled) return;
        setDraft(null);
        setInitial(null);
        setLoadError('Case request id is missing or invalid.');
        setLoadedForKey(loadKey);
        return;
      }

      if (useSharedViewData) {
        if (viewDataLoading) return;
        if (!viewData?.draft) {
          if (cancelled) return;
          setDraft(null);
          setInitial(null);
          setStep4(undefined);
          setLoadError('Could not load case update data.');
          setLoadedForKey(loadKey);
          return;
        }
        if (cancelled) return;
        const loaded = cloneDraft(viewData.draft);
        setDraft(loaded);
        setInitial(cloneDraft(loaded));
        setStep4(viewData.step4);
        setSubmittedLabel(formatSubmittedAtLabel(viewData.submittedAt, recentSubmittedAt));
        setLoadError('');
        setLoadedForKey(loadKey);
        return;
      }

      const res = await getCivilAppellateRequestViewData(caseRequestId);
      if (cancelled) return;
      if (!res.success || !res.data?.draft) {
        setDraft(null);
        setInitial(null);
        setStep4(undefined);
        setLoadError((!res.success ? res.message : undefined) ?? 'Could not load case update data.');
        setLoadedForKey(loadKey);
        return;
      }
      const loaded = cloneDraft(res.data.draft);
      setDraft(loaded);
      setInitial(cloneDraft(loaded));
      setStep4(res.data.step4);
      setSubmittedLabel(formatSubmittedAtLabel(res.data.submittedAt, recentSubmittedAt));
      setLoadError('');
      setLoadedForKey(loadKey);
    })();
    return () => {
      cancelled = true;
    };
  }, [caseRequestId, loadKey, recentSubmittedAt, useSharedViewData, viewData, viewDataLoading]);

  const canSaveStep1 = useMemo(() => canSaveStep(1, draft, initial), [draft, initial]);
  const canSaveStep2 = useMemo(() => canSaveStep(2, draft, initial), [draft, initial]);
  const canSaveStep3 = useMemo(() => canSaveStep(3, draft, initial), [draft, initial]);
  const clearErr = (key: string) => setErrors((e) => ({ ...e, [key]: '' }));

  const saveSection = async (step: 1 | 2 | 3) => {
    if (!draft || readOnly || saveInFlightRef.current) return;
    const result = validateCivilAppellateStep(step, draft, [], lookups.corporateInstitutes);
    const errors = omitCaseUpdateUploadErrors(result.errors);
    const specs = omitCaseUpdateUploadSpecs(result.specs);
    setErrors(errors);
    if (Object.keys(errors).length) {
      focusFirstInvalid(specs, errors);
      return;
    }

    saveInFlightRef.current = true;
    setSavingStep(step);
    try {
      const r = await saveCivilAppellateRequest(draft, {
        isDraft: false,
        confirmedCorrect: true,
        currentStep: step,
        isUpdateAlreadySubmitted: true,
      });
      if (!r.success) {
        setFlashToast({ message: r.message ?? 'Failed to save case update.', variant: 'error' });
        return;
      }
      const next = {
        ...draft,
        draftRequestId: r.data.requestId || draft.draftRequestId,
        referenceNo: r.data.fileReferenceNumber ?? draft.referenceNo,
      };
      setDraft(next);
      setInitial((prev) => {
        if (!prev) return cloneDraft(next);
        const pick =
          step === 1 ? pickStep1Snapshot : step === 2 ? pickStep2Snapshot : pickStep3Snapshot;
        return mergeSavedStepIntoInitialBaseline(prev, next, pick, cloneDraft);
      });
      setErrors({});
      setFlashToast({ message: 'Case update saved successfully.', variant: 'success' });
    } finally {
      saveInFlightRef.current = false;
      setSavingStep(null);
    }
  };

  const resetSection = () => {
    if (initial) {
      setDraft(cloneDraft(initial));
      setErrors({});
    }
  };

  const beginCancel = () => {
    if (isSaving || !draft || !initial) return;
    if (draftHasUnsavedChanges(draft, initial)) {
      setCancelConfirmOpen(true);
      return;
    }
    resetSection();
  };

  if (loading) return <p className="tcd-update-ts">Loading case update…</p>;
  if (!draft) {
    return (
      <div className="tcd-case-update-error" role="alert">
        <p className="tcd-update-ts" style={{ color: 'var(--red)', margin: 0 }}>
          {loadError || 'Could not load case update data.'}
        </p>
      </div>
    );
  }

  const submitter = submitterFromApiStep(step4);
  const deliveryLabel = deliveryMethodDisplay(
    draft as unknown as CriminalWizardDraft,
    lookups.deliveryMethods,
    step4 as CriminalSaveStep4 | undefined,
  );
  const instituteLabel =
    draft.instituteType === 'corporation'
      ? isOtherCorporateInstitute(draft.corporateInstituteId, draft.corporateInstituteName)
        ? [draft.corporateInstituteOther, draft.corporateInstituteName].filter(Boolean).join(' · ') ||
          'Corporation'
        : draft.corporateInstituteName || 'Corporation'
      : draft.instituteType === 'government'
        ? 'Government Department'
        : '—';
  const urgencyJustificationRequired = isHighUrgencyLevel(draft.urgencyLevelName);
  const bodyTypeLabel = draft.isIncorporatedBody
    ? INCORPORATED_BODY_TYPE_LABELS.incorporated
    : INCORPORATED_BODY_TYPE_LABELS.unincorporated;

  return (
    <>
      <ConfirmDialog
        isOpen={cancelConfirmOpen}
        title="Cancel Changes"
        message="Are you sure you want to cancel? Any unsaved changes will be lost."
        onClose={() => setCancelConfirmOpen(false)}
        onConfirm={() => {
          confirmCaseUpdateCancel(resetSection, () => setCancelConfirmOpen(false), onCancelConfirmed);
        }}
      />

      <div className="tcd-case-update">
        <p className="tcd-update-ts">
          Request Submitted at:{' '}
          <strong>{submittedLabel || formatDisplayDateTime(recentSubmittedAt) || '—'}</strong>
        </p>

        <ReviewSection step="Step 01" title="Case Overview">
          <ReviewSubBlock title="Case Categorization">
            <ReviewGrid cols={4}>
              <ReviewField label="File Category" value={draft.fileCategory} />
              <ReviewField label="Sub File Category" value={draft.subFileCategoryName} />
              <ReviewField label="Institute Type" value={instituteLabel} />
            </ReviewGrid>
            <ReviewChips items={draft.applicableAreas} />
          </ReviewSubBlock>

          <ReviewSubBlock title="Case Details">
            <div className="ccr-fg" style={{ marginBottom: 14 }}>
              <label>
                Description <span className="req">*</span>
              </label>
              <textarea
                id="capl-description"
                className={inpClass(Boolean(errors.description))}
                rows={4}
                maxLength={MAX_DESC}
                value={draft.description}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) => {
                  setDraft((d) => (d ? { ...d, description: sliceMax(e.target.value, MAX_DESC) } : d));
                  clearErr('description');
                }}
              />
              <FieldError message={errors.description} />
            </div>

            <div className="ccr-fr c2">
              <div className="ccr-fg" id="capl-appealMatter">
                <label>
                  Appeal Matter <span className="req">*</span>
                </label>
                <div className="ccr-radio-row">
                  {(['yes', 'no'] as const).map((v) => (
                    <label key={v} className="ccr-radio">
                      <input
                        type="radio"
                        name="capl-appealMatter"
                        checked={draft.isAppealMatter === v}
                        disabled={readOnly}
                        onChange={() => {
                          setDraft((d) =>
                            d
                              ? {
                                  ...d,
                                  isAppealMatter: v,
                                  originalCourt: v === 'yes' ? d.originalCourt : '',
                                  judgmentOrOrderDocuments:
                                    v === 'yes' ? d.judgmentOrOrderDocuments : [],
                                }
                              : d,
                          );
                          clearErr('isAppealMatter');
                          if (v === 'no') {
                            clearErr('originalCourt');
                            clearErr('judgmentOrOrderDocuments');
                          }
                        }}
                      />
                      {v === 'yes' ? 'Yes' : 'No'}
                    </label>
                  ))}
                </div>
                <FieldError message={errors.isAppealMatter} />
              </div>
              {draft.isAppealMatter === 'yes' ? (
                <>
                  <div className="ccr-fg">
                    <label>
                      Original Court <span className="req">*</span>
                    </label>
                    <input
                      id="capl-originalCourt"
                      className={inpClass(Boolean(errors.originalCourt))}
                      value={draft.originalCourt}
                      readOnly={readOnly}
                      disabled={readOnly}
                      maxLength={MAX_REF}
                      onChange={(e) => {
                        setDraft((d) =>
                          d ? { ...d, originalCourt: sliceMax(e.target.value, MAX_REF) } : d,
                        );
                        clearErr('originalCourt');
                      }}
                    />
                    <FieldError message={errors.originalCourt} />
                  </div>
                  <div className="ccr-fg" style={{ gridColumn: '1 / -1' }}>
                    <label>Judgement/Order</label>
                    <ReviewUploadedFilesTable
                      files={draft.judgmentOrOrderDocuments}
                      emptyMessage="—"
                    />
                  </div>
                </>
              ) : null}
              <div className="ccr-fg">
                <label>
                  Province <span className="req">*</span>
                </label>
                <WizardSelect
                  id="capl-province"
                  hasError={Boolean(errors.provinceId)}
                  value={draft.provinceId}
                  disabled={readOnly}
                  placeholder="Select Province"
                  options={toSelectOpts(lookups.provinces)}
                  onChange={(id) => {
                    const name = lookups.provinces.find((p) => p.id === id)?.name ?? '';
                    setDraft((d) =>
                      d
                        ? {
                            ...d,
                            provinceId: id,
                            provinceName: name,
                            districtId: '',
                            districtName: '',
                          }
                        : d,
                    );
                    clearErr('provinceId');
                  }}
                />
                <FieldError message={errors.provinceId} />
              </div>
              <div className="ccr-fg">
                <label>
                  District <span className="req">*</span>
                </label>
                <WizardSelect
                  id="capl-district"
                  hasError={Boolean(errors.districtId)}
                  value={draft.districtId}
                  disabled={readOnly || !draft.provinceId}
                  placeholder={draft.provinceId ? 'Select District' : 'Select province first'}
                  options={toSelectOpts(lookups.districts)}
                  onChange={(id) => {
                    const name = lookups.districts.find((x) => x.id === id)?.name ?? '';
                    setDraft((d) => (d ? { ...d, districtId: id, districtName: name } : d));
                    clearErr('districtId');
                  }}
                />
                <FieldError message={errors.districtId} />
              </div>
              <div className="ccr-fg">
                <label>
                  Your Reference Number (External) <span className="req">*</span>
                </label>
                <input
                  id="capl-yourRef"
                  className={inpClass(Boolean(errors.yourReferenceNumber))}
                  value={draft.yourReferenceNumber}
                  readOnly={readOnly}
                  disabled={readOnly}
                  maxLength={MAX_REF}
                  onChange={(e) => {
                    setDraft((d) =>
                      d ? { ...d, yourReferenceNumber: sliceMax(e.target.value, MAX_REF) } : d,
                    );
                    clearErr('yourReferenceNumber');
                  }}
                />
                <FieldError message={errors.yourReferenceNumber} />
              </div>
              <div className="ccr-fg">
                <label>Other External Reference Number(s)</label>
                <input
                  className={inpClass(Boolean(errors.otherExternalReferences))}
                  value={draft.otherExternalReferences}
                  readOnly={readOnly}
                  disabled={readOnly}
                  maxLength={MAX_REF}
                  onChange={(e) =>
                    setDraft((d) =>
                      d ? { ...d, otherExternalReferences: sliceMax(e.target.value, MAX_REF) } : d,
                    )
                  }
                />
                <FieldError message={errors.otherExternalReferences} />
              </div>
              <div className="ccr-fg">
                <label>Case Number</label>
                <input
                  className={inpClass(Boolean(errors.caseNumber))}
                  value={draft.caseNumber}
                  readOnly={readOnly}
                  disabled={readOnly}
                  maxLength={MAX_REF}
                  onChange={(e) =>
                    setDraft((d) => (d ? { ...d, caseNumber: sliceMax(e.target.value, MAX_REF) } : d))
                  }
                />
                <FieldError message={errors.caseNumber} />
              </div>
              <div className="ccr-fg" id="capl-connected">
                <label>
                  Are there any connected matters (Cases)? <span className="req">*</span>
                </label>
                <div className="ccr-radio-row">
                  {(['yes', 'no'] as const).map((v) => (
                    <label key={v} className="ccr-radio">
                      <input
                        type="radio"
                        name="capl-connected"
                        checked={draft.hasConnectedMatters === v}
                        disabled={readOnly}
                        onChange={() => {
                          setDraft((d) =>
                            d
                              ? {
                                  ...d,
                                  hasConnectedMatters: v,
                                  connectedCaseNumber: v === 'yes' ? d.connectedCaseNumber : '',
                                  connectedAgdFileRefAvailable:
                                    v === 'yes' ? d.connectedAgdFileRefAvailable : false,
                                  connectedAgdFileRef: v === 'yes' ? d.connectedAgdFileRef : '',
                                }
                              : d,
                          );
                          clearErr('hasConnectedMatters');
                        }}
                      />
                      {v === 'yes' ? 'Yes' : 'No'}
                    </label>
                  ))}
                </div>
                <FieldError message={errors.hasConnectedMatters} />
              </div>
              {draft.hasConnectedMatters === 'yes' ? (
                <>
                  <div className="ccr-fg">
                    <label>
                      Connected Case Number <span className="req">*</span>
                    </label>
                    <input
                      id="capl-connectedCase"
                      className={inpClass(Boolean(errors.connectedCaseNumber))}
                      value={draft.connectedCaseNumber}
                      readOnly={readOnly}
                      disabled={readOnly}
                      maxLength={MAX_REF}
                      onChange={(e) => {
                        setDraft((d) =>
                          d ? { ...d, connectedCaseNumber: sliceMax(e.target.value, MAX_REF) } : d,
                        );
                        clearErr('connectedCaseNumber');
                      }}
                    />
                    <FieldError message={errors.connectedCaseNumber} />
                  </div>
                  <div className="ccr-fg">
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      Connected AGD File Reference Number
                      {draft.connectedAgdFileRefAvailable ? <span className="req">*</span> : null}
                      {!readOnly ? (
                        <>
                          <input
                            type="checkbox"
                            style={{ width: 15, height: 15, accentColor: '#000', cursor: 'pointer' }}
                            checked={draft.connectedAgdFileRefAvailable}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setDraft((d) =>
                                d
                                  ? {
                                      ...d,
                                      connectedAgdFileRefAvailable: checked,
                                      connectedAgdFileRef: checked ? d.connectedAgdFileRef : '',
                                    }
                                  : d,
                              );
                              clearErr('connectedAgdFileRef');
                            }}
                          />
                          <span>Available</span>
                        </>
                      ) : null}
                    </label>
                    <input
                      id="capl-connectedAgd"
                      className={inpClass(Boolean(errors.connectedAgdFileRef))}
                      value={draft.connectedAgdFileRef}
                      readOnly={readOnly}
                      disabled={readOnly || !draft.connectedAgdFileRefAvailable}
                      maxLength={MAX_REF}
                      onChange={(e) => {
                        setDraft((d) =>
                          d ? { ...d, connectedAgdFileRef: sliceMax(e.target.value, MAX_REF) } : d,
                        );
                        clearErr('connectedAgdFileRef');
                      }}
                    />
                    <FieldError message={errors.connectedAgdFileRef} />
                  </div>
                </>
              ) : null}
            </div>
          </ReviewSubBlock>

          <TaskCaseUpdateSectionActions
            canSave={canSaveStep1}
            saving={savingStep === 1}
            readOnly={readOnly}
            onSave={() => void saveSection(1)}
            onCancel={beginCancel}
          />
        </ReviewSection>

        <ReviewSection step="Step 02" title="Party Details">
          <ReviewSubBlock title="Party Details">
            <div className="ccr-fr c2">
              <div className="ccr-fg">
                <label>
                  Plaintiff / Applicant Name <span className="req">*</span>
                </label>
                <input
                  id="capl-plaintiff"
                  className={inpClass(Boolean(errors.plaintiffName))}
                  value={draft.plaintiffName}
                  readOnly={readOnly}
                  disabled={readOnly}
                  maxLength={MAX_REF}
                  onChange={(e) => {
                    setDraft((d) =>
                      d ? { ...d, plaintiffName: sliceMax(e.target.value, MAX_REF) } : d,
                    );
                    clearErr('plaintiffName');
                  }}
                />
                <FieldError message={errors.plaintiffName} />
              </div>
              <div className="ccr-fg">
                <label>
                  Defendant / Respondent Name <span className="req">*</span>
                </label>
                <input
                  id="capl-defendant"
                  className={inpClass(Boolean(errors.defendantName))}
                  value={draft.defendantName}
                  readOnly={readOnly}
                  disabled={readOnly}
                  maxLength={MAX_REF}
                  onChange={(e) => {
                    setDraft((d) =>
                      d ? { ...d, defendantName: sliceMax(e.target.value, MAX_REF) } : d,
                    );
                    clearErr('defendantName');
                  }}
                />
                <FieldError message={errors.defendantName} />
              </div>
              <div className="ccr-fg" id="capl-institutionType" style={{ gridColumn: '1 / -1' }}>
                <label>
                  Select one of the following <span className="req">*</span>
                </label>
                {readOnly ? (
                  <p style={{ margin: 0 }}>{bodyTypeLabel}</p>
                ) : (
                  <div
                    className="ccr-radio-row"
                    style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 10 }}
                  >
                    <label className="ccr-radio">
                      <input
                        type="radio"
                        name="capl-institutionType"
                        checked={!draft.isIncorporatedBody}
                        onChange={() => {
                          setDraft((d) => (d ? { ...d, isIncorporatedBody: false } : d));
                          clearErr('isIncorporatedBody');
                        }}
                      />
                      {INCORPORATED_BODY_TYPE_LABELS.unincorporated}
                    </label>
                    <label className="ccr-radio">
                      <input
                        type="radio"
                        name="capl-institutionType"
                        checked={draft.isIncorporatedBody}
                        onChange={() => {
                          setDraft((d) => (d ? { ...d, isIncorporatedBody: true } : d));
                          clearErr('isIncorporatedBody');
                        }}
                      />
                      {INCORPORATED_BODY_TYPE_LABELS.incorporated}
                    </label>
                  </div>
                )}
                <FieldError message={errors.isIncorporatedBody} />
              </div>
              <div className="ccr-fg" style={{ gridColumn: '1 / -1' }}>
                <label>Other Involved Institutions</label>
                <textarea
                  className={inpClass(Boolean(errors.otherInvolvedInstitutions))}
                  rows={3}
                  maxLength={MAX_DESC}
                  value={draft.otherInvolvedInstitutions}
                  readOnly={readOnly}
                  disabled={readOnly}
                  onChange={(e) =>
                    setDraft((d) =>
                      d
                        ? { ...d, otherInvolvedInstitutions: sliceMax(e.target.value, MAX_DESC) }
                        : d,
                    )
                  }
                />
                <FieldError message={errors.otherInvolvedInstitutions} />
              </div>
              <div className="ccr-fg">
                <label>
                  Name of the Legal Officer/ Officer In-Charge <span className="req">*</span>
                </label>
                <input
                  id="capl-legalOfficer"
                  className={inpClass(Boolean(errors.legalOfficerName))}
                  value={draft.legalOfficerName}
                  readOnly={readOnly}
                  disabled={readOnly}
                  maxLength={MAX_REF}
                  onChange={(e) => {
                    setDraft((d) =>
                      d ? { ...d, legalOfficerName: sliceMax(e.target.value, MAX_REF) } : d,
                    );
                    clearErr('legalOfficerName');
                  }}
                />
                <FieldError message={errors.legalOfficerName} />
              </div>
              <div className="ccr-fg">
                <label>
                  Contact Number <span className="req">*</span>
                </label>
                <input
                  id="capl-legalContact"
                  className={inpClass(Boolean(errors.legalOfficerContact))}
                  inputMode="numeric"
                  value={draft.legalOfficerContact}
                  readOnly={readOnly}
                  disabled={readOnly}
                  onChange={(e) => {
                    setDraft((d) =>
                      d
                        ? {
                            ...d,
                            legalOfficerContact: e.target.value
                              .replace(/\D/g, '')
                              .slice(0, MAX_CONTACT),
                          }
                        : d,
                    );
                    clearErr('legalOfficerContact');
                  }}
                />
                <FieldError message={errors.legalOfficerContact} />
              </div>
            </div>
          </ReviewSubBlock>

          <TaskCaseUpdateSectionActions
            canSave={canSaveStep2}
            saving={savingStep === 2}
            readOnly={readOnly}
            onSave={() => void saveSection(2)}
            onCancel={beginCancel}
          />
        </ReviewSection>

        <ReviewSection step="Step 03" title="Timeline">
          <ReviewSubBlock title="Timeline">
            <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
              <div className="ccr-fg">
                <label>
                  Urgency Level <span className="req">*</span>
                </label>
                <WizardSelect
                  id="capl-urgency"
                  hasError={Boolean(errors.urgencyLevelId)}
                  value={draft.urgencyLevelId}
                  disabled={readOnly}
                  placeholder="Select Urgency Level"
                  options={toSelectOpts(lookups.urgencyLevels)}
                  onChange={(id) => {
                    const name = lookups.urgencyLevels.find((u) => u.id === id)?.name ?? '';
                    setDraft((d) => (d ? { ...d, urgencyLevelId: id, urgencyLevelName: name } : d));
                    if (!isHighUrgencyLevel(name)) clearErr('urgencyJustification');
                    clearErr('urgencyLevelId');
                  }}
                />
                <FieldError message={errors.urgencyLevelId} />
              </div>
              <div className="ccr-fg">
                <label>Deadline (if any)</label>
                <FormattedDateInput
                  id="capl-deadline"
                  className={inpClass(Boolean(errors.deadline))}
                  value={draft.deadline}
                  minDate={minDeadlineDate}
                  enableYmdViews
                  disabled={readOnly}
                  onChange={(next) => {
                    setDraft((d) => (d ? { ...d, deadline: next } : d));
                    clearErr('deadline');
                  }}
                />
                <FieldError message={errors.deadline} />
              </div>
            </div>
            <div className="ccr-fg">
              <label>
                Urgency Justification{' '}
                {urgencyJustificationRequired ? <span className="req">*</span> : null}
              </label>
              <textarea
                id="capl-urgencyJustification"
                className={inpClass(Boolean(errors.urgencyJustification))}
                rows={3}
                maxLength={MAX_TEXT}
                value={draft.urgencyJustification}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) => {
                  setDraft((d) =>
                    d ? { ...d, urgencyJustification: sliceMax(e.target.value, MAX_TEXT) } : d,
                  );
                  clearErr('urgencyJustification');
                }}
              />
              <FieldError message={errors.urgencyJustification} />
            </div>
          </ReviewSubBlock>

          <TaskCaseUpdateSectionActions
            canSave={canSaveStep3}
            saving={savingStep === 3}
            readOnly={readOnly}
            onSave={() => void saveSection(3)}
            onCancel={beginCancel}
          />
        </ReviewSection>

        <ReviewSection step="Step 04" title="Submission & Physical File Delivery">
          <ReviewSubBlock title="Request Submitter's Details">
            <ReviewGrid cols={4}>
              <ReviewField label="Submitted Agency Name" value={submitter.agency} />
              <ReviewField label="Submitted Division/ Unit" value={submitter.division} />
              <ReviewField label="Designation" value={submitter.designation} />
              <ReviewField label="Submitted by" value={submitter.name} />
              <span className="ccr-review-grid-spacer" aria-hidden="true" />
              <ReviewField label="Email Address" value={submitter.email} />
              <ReviewField label="Contact Number" value={submitter.contact} />
            </ReviewGrid>
          </ReviewSubBlock>
          <ReviewSubBlock title="Physical File Delivery Method">
            <ReviewField label="Physical File Delivery Method" value={deliveryLabel} fullWidth />
            {draft.deliveryOther.trim() ? (
              <ReviewField label="Other Delivery Method" value={draft.deliveryOther} />
            ) : null}
          </ReviewSubBlock>
        </ReviewSection>
      </div>
    </>
  );
}
